import React, { useContext, useMemo, useReducer, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Link } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faBellSlash,
  faChevronDown,
  faChevronUp,
  faCircleNotch,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { EDIT_REMINDER } from '../../Constants/AssetModel';
import {
  ASSET_MODEL,
  ASSET_MODEL_READ,
  NON_PLATFORM_USER_SUBSCRIPTION,
  USER,
} from '../../Constants/RBAC';
import { Width } from '../../Context/WidthContext';

import { hasWriteAccessToResource } from '../../RBAC/AccessTools';

import NonPlatformUserSubscriptionModal from './NonPlatformUserSubscriptionModal';
import Can from '../../RBAC/Can';
import { TEMPLATE_WEATHER_TYPE } from '../../Constants/Rule';

function filterUsers(search = '', u, currentOrgUsersMap) {
  if (!search.length) return true;

  const isPlatformUser = uuidValidate(u);
  if (!isPlatformUser) {
    return u.toLowerCase().includes(search.toLowerCase());
  }

  const { firstName, lastName } = currentOrgUsersMap.get(u) || {};
  let firstNameMatch;
  let lastNameMatch;

  if (firstName) {
    firstNameMatch = firstName.toLowerCase().includes(search.toLowerCase());
  }
  if (lastName) {
    lastNameMatch = lastName.toLowerCase().includes(search.toLowerCase());
  }
  return !!firstNameMatch || !!lastNameMatch;
}

function parseUserNotificationPreferences(selectedUser, currentUser) {
  if (!selectedUser?._id) {
    const isEmailSub = selectedUser?.Destination?.includes?.('@');
    return { emailIsDisabled: !isEmailSub, smsIsDisabled: isEmailSub };
  }

  const preferences = {
    emailIsDisabled: false,
    smsIsDisabled: !selectedUser.phoneNumber,
  };
  if (selectedUser?._id === currentUser?._id) return preferences;

  const { email, sms } = selectedUser?.settings?.notifications ?? {};
  if (email === false) preferences.emailIsDisabled = true;
  if (sms === true) preferences.smsIsDisabled = false;
  return preferences;
}

const initSubsTypeDisplayState = {
  showDmSubs: false,
  showMaintenanceSubs: false,
  showRuleSubs: false,
};

export default function AssetSubscriptionsDisplay(props) {
  const {
    asset,
    currentOrg: { Users = [] } = {},
    currentUser,
    currentUserRef,
    error,
    handleNonPlatformSubAdded,
    handleNonPlatformSubRemove,
    handleSubscriptionClick,
    handleBatchClick,
    maintenanceState: { count, instances } = {},
    ruleState: { Count, Rules },
    selectedUser,
    setSelectedUser,
    showSubscribeNonPlatformUserModal,
    subscriptions,
    subscriptionPending,
    templateID,
    toggleNonPlatformSubscriptionModal,
  } = props;

  const width = useContext(Width);

  const [search, updateSearch] = useState(''),
    [subsTypeDisplayState, dispatchSubsTypesDisplayState] = useReducer(
      displayToggleReducer,
      initSubsTypeDisplayState
    );
  const { emailIsDisabled, smsIsDisabled } = parseUserNotificationPreferences(
    selectedUser,
    currentUser
  );
  const currentOrgUsersMap = useMemo(() => {
    return new Map(Users.map((u) => [u._id, u]));
  }, [Users]);

  const handleUserSelect = (u, isPlatformUser) => {
    if (!isPlatformUser) return setSelectedUser({ Destination: u });

    const { _id, firstName, lastName, phoneNumber, settings } =
      currentOrgUsersMap.get(u) || {};
    setSelectedUser({ _id, firstName, lastName, phoneNumber, settings });
  };

  const formatNameForList = (u, isPlatformUser) => {
    if (!isPlatformUser) return u;
    const { firstName = '', lastName = '' } = currentOrgUsersMap.get(u) || {};
    return `${firstName} ${lastName}`;
  };

  const showEmailSubs =
    selectedUser?._id || selectedUser?.Destination?.includes?.('@');
  const showSmsSubs =
    selectedUser?._id || !selectedUser?.Destination?.includes?.('@');

  const hasDmEmailSub = useMemo(() => {
    const { _id, Destination } = selectedUser || {};
    if (!_id && !Destination) return false;

    const subs = subscriptions.get(_id || Destination) || [];
    return subs.some((s) => s.includes('*:email'));
  }, [selectedUser, subscriptions]);
  const hasDmSmsSub = useMemo(() => {
    const { _id, Destination } = selectedUser || {};
    if (!_id && !Destination) return false;

    const subs = subscriptions.get(_id || Destination) || [];
    return subs.some((s) => s.includes('*:sms'));
  }, [selectedUser, subscriptions]);

  return (
    <div className="position-absolute overflow-auto h-100 w-100 d-flex flex-column bg-white p-1 p-md-3 p-lg-4">
      <div className="d-flex align-items-center mb-3">
        <h6 className="mb-md-0">Subscriptions</h6>
        <p className="mb-md-0 mx-md-2 three-quarter-text text-danger">
          {error}
        </p>
        <Can
          no={() => null}
          orgID={asset?.orgID}
          perform={NON_PLATFORM_USER_SUBSCRIPTION}
          resource={USER}
          yes={() => (
            <button
              className="btn modal-button-primary py-0 px-1 px-md-3 ml-auto three-quarter-text"
              onClick={toggleNonPlatformSubscriptionModal}
            >
              <span className="d-none d-md-inline">Subscribe </span>Non-platform
              User
            </button>
          )}
        />
      </div>
      <span className="d-block three-quarter-text mb-1 mb-md-3">
        Set your subscription preferences for every asset this trigger affects.
        You can subscribe other users to this Rule if you have access. Manage
        your subscriptions in your settings.
      </span>
      <div className="inner-subscriptions-wrapper flex-grow-1 rounded-lg border border-icon-link-gray">
        <div className="users-container">
          <p className="mb-0 p-2 pl-md-3 bg-background-gray">Users</p>
          <div className="p-1 p-md-3">
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span
                  className="input-group-text bg-transparent border-0"
                  id="searchUsers"
                >
                  <FontAwesomeIcon
                    className="text-text-light-gray"
                    icon={faSearch}
                  />
                </span>
                <input
                  type="text"
                  className="form-control border-0"
                  aria-label="user name search"
                  aria-describedby="searchUsers"
                  placeholder="Find users"
                  onChange={(e) => updateSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              className={`btn btn-link text-decoration-none w-100 text-left rounded ${
                currentUser?._id === selectedUser._id
                  ? 'modal-button-primary'
                  : 'text-dark'
              }`}
              onClick={() =>
                setSelectedUser({
                  _id: currentUser?._id,
                  firstName: currentUser?.firstName,
                  lastName: currentUser?.lastName,
                  phoneNumber: currentUser?.phoneNumber,
                  settings: currentUser?.settings,
                })
              }
              ref={currentUserRef}
            >
              {currentUser?._id
                ? `${currentUser?.firstName || ''} ${
                    currentUser?.lastName || ''
                  } (you)`
                : ''}
            </button>
            <hr />
            {subscriptions.size && hasWriteAccessToResource(asset?.orgID, USER)
              ? [...subscriptions.keys()]
                  .filter((u) => {
                    if (u === currentUser?._id) return false;
                    return filterUsers(search, u, currentOrgUsersMap);
                  })
                  .map((u) => {
                    const isPlatformUser = uuidValidate(u);
                    return (
                      <div
                        className={`d-flex align-items-stretch w-100 rounded ${
                          [
                            selectedUser?._id,
                            selectedUser?.Destination,
                          ].includes(u)
                            ? 'modal-button-primary'
                            : 'text-dark'
                        } three-quarter-text`}
                        key={u}
                      >
                        <button
                          className="btn btn-link text-decoration-none text-left text-reset flex-fill"
                          onClick={() => handleUserSelect(u, isPlatformUser)}
                        >
                          <p className="mb-0">
                            {formatNameForList(u, isPlatformUser)}
                          </p>
                          <small
                            className={
                              isPlatformUser
                                ? 'd-none'
                                : u === selectedUser?.Destination
                                  ? 'text-white'
                                  : 'text-text-light-gray'
                            }
                          >
                            non-elevat user
                          </small>
                        </button>
                        <button
                          className={
                            isPlatformUser
                              ? 'd-none'
                              : 'btn btn-link d-flex align-items-center justify-content-center text-reset'
                          }
                          disabled={subscriptionPending}
                          onClick={() => handleNonPlatformSubRemove(u)}
                        >
                          <FontAwesomeIcon
                            icon={
                              subscriptionPending === u
                                ? faCircleNotch
                                : faTimes
                            }
                            spin={subscriptionPending === u}
                          />
                        </button>
                      </div>
                    );
                  })
              : null}
          </div>
        </div>
        <div className="rule-dm1-maintenance-wrapper">
          <div>
            <div className="bg-background-gray p-2 pl-md-3 d-flex align-items-center">
              <button
                className="btn btn-link p-0 mr-3 text-dark"
                onClick={() => dispatchSubsTypesDisplayState({ type: 'dm1' })}
                style={{ lineHeight: 1 }}
              >
                <FontAwesomeIcon
                  icon={
                    subsTypeDisplayState.showDmSubs
                      ? faChevronUp
                      : faChevronDown
                  }
                />
              </button>
              DM1 Faults
            </div>
            {selectedUser?._id && (emailIsDisabled || smsIsDisabled) ? (
              <div className="text-warning three-quarter-text ml-md-3">{`User has a preference set not to receive ${
                emailIsDisabled && smsIsDisabled
                  ? 'email or SMS notifications'
                  : emailIsDisabled
                    ? 'email notifications'
                    : smsIsDisabled
                      ? 'SMS notifications or has not added a phone number'
                      : ''
              }.`}</div>
            ) : null}
            <div
              className={subsTypeDisplayState.showDmSubs ? 'd-flex' : 'd-none'}
            >
              <div
                className={
                  showEmailSubs
                    ? 'd-flex flex-column align-items-center p-4 ml-1'
                    : 'd-none'
                }
              >
                <span>Email</span>
                <button
                  className={`btn btn-link ${
                    hasDmEmailSub
                      ? 'text-brand-primary-alert'
                      : 'text-background-gray'
                  }`}
                  onClick={() => handleSubscriptionClick('*:email')}
                  disabled={subscriptionPending || emailIsDisabled}
                >
                  <FontAwesomeIcon
                    icon={hasDmEmailSub ? faBell : faBellSlash}
                  />
                </button>
              </div>
              <div
                className={
                  showSmsSubs
                    ? 'd-flex flex-column align-items-center p-4 ml-1'
                    : 'd-none'
                }
              >
                <span>SMS</span>
                <button
                  className={`btn btn-link ${
                    hasDmSmsSub
                      ? 'text-brand-primary-alert'
                      : 'text-background-gray'
                  }`}
                  onClick={() => handleSubscriptionClick('*:sms')}
                  disabled={subscriptionPending || smsIsDisabled}
                >
                  <FontAwesomeIcon icon={hasDmSmsSub ? faBell : faBellSlash} />
                </button>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-background-gray p-2 pl-md-3 d-flex align-items-center">
              <button
                className="btn btn-link p-0 mr-3 text-dark"
                onClick={() => dispatchSubsTypesDisplayState({ type: 'rule' })}
                style={{ lineHeight: 1 }}
              >
                <FontAwesomeIcon
                  icon={
                    subsTypeDisplayState.showRuleSubs
                      ? faChevronUp
                      : faChevronDown
                  }
                />
              </button>
              Rules
              <span className="ml-3">{Count}</span>
            </div>
            {selectedUser?._id && (emailIsDisabled || smsIsDisabled) ? (
              <div className="text-warning three-quarter-text ml-md-3">{`User has a preference set not to receive ${
                emailIsDisabled && smsIsDisabled
                  ? 'email or SMS notifications'
                  : emailIsDisabled
                    ? 'email notifications'
                    : smsIsDisabled
                      ? 'SMS notifications or has not added a phone number'
                      : ''
              }.`}</div>
            ) : null}
            <table
              className={
                subsTypeDisplayState.showRuleSubs
                  ? 'table table-borderless'
                  : 'd-none'
              }
            >
              <thead>
                <tr>
                  {showEmailSubs ? (
                    <th scope="col" className="col-notification text-center">
                      <button
                        className="btn btn-sm btn-outline-icon-link-gray bg-white text-muted rounded-lg"
                        disabled={emailIsDisabled || subscriptionPending}
                        onClick={() => handleBatchClick('email', 'gateway')}
                      >
                        Email
                      </button>
                    </th>
                  ) : null}
                  {showSmsSubs ? (
                    <th scope="col" className="col-notification text-center">
                      <button
                        className="btn btn-sm btn-outline-icon-link-gray bg-white text-muted rounded-lg"
                        disabled={smsIsDisabled || subscriptionPending}
                        onClick={() => handleBatchClick('sms', 'gateway')}
                      >
                        SMS
                      </button>
                    </th>
                  ) : null}
                  <th scope="col" className="col-asset-name"></th>
                </tr>
              </thead>
              <tbody>
                {Rules?.map(({ Id, Name = '', Type }) => {
                  const hasRuleEmailSub = () => {
                    const { _id, Destination } = selectedUser || {};
                    if (!_id && !Destination) return false;

                    const subs = subscriptions.get(_id || Destination) || [];
                    return subs.some((s) => s.includes(`${Id}:email`));
                  };
                  const hasRuleSmsSub = () => {
                    const { _id, Destination } = selectedUser || {};
                    if (!_id && !Destination) return false;

                    const subs = subscriptions.get(_id || Destination) || [];
                    return subs.some((s) => s.includes(`${Id}:sms`));
                  };

                  const isWeatherRule = Type === TEMPLATE_WEATHER_TYPE;
                  return (
                    <tr key={Id}>
                      {showEmailSubs ? (
                        <td className="text-center">
                          <span
                            data-for={`${Id}:email`}
                            data-tip
                            data-tip-disable={false}
                          >
                            <button
                              className={`btn btn-link p-0 ${
                                hasRuleEmailSub()
                                  ? 'text-brand-primary-alert'
                                  : 'text-background-gray'
                              }`}
                              onClick={() =>
                                handleSubscriptionClick(`${Id}:email`)
                              }
                              disabled={
                                subscriptionPending ||
                                emailIsDisabled ||
                                isWeatherRule
                              }
                            >
                              <FontAwesomeIcon
                                icon={hasRuleEmailSub() ? faBell : faBellSlash}
                              ></FontAwesomeIcon>
                              {isWeatherRule ? (
                                <ReactTooltip id={`${Id}:email`}>
                                  <span>
                                    Email notifications not supported for
                                    Weather Alerts
                                  </span>
                                </ReactTooltip>
                              ) : null}
                            </button>
                          </span>
                        </td>
                      ) : null}
                      {showSmsSubs ? (
                        <td
                          className="text-center"
                          data-tip={
                            smsIsDisabled
                              ? 'This user has a preference set to not receive SMS notifications'
                              : ''
                          }
                        >
                          <button
                            className={`btn btn-link p-0 ${
                              hasRuleSmsSub()
                                ? 'text-brand-primary-alert'
                                : 'text-background-gray'
                            }`}
                            onClick={() => handleSubscriptionClick(`${Id}:sms`)}
                            disabled={subscriptionPending || smsIsDisabled}
                          >
                            <FontAwesomeIcon
                              icon={hasRuleSmsSub() ? faBell : faBellSlash}
                            />
                          </button>
                        </td>
                      ) : null}
                      <td>
                        <Can
                          no={() => (
                            <button className="btn btn-link p-0" disabled>
                              {Name}
                            </button>
                          )}
                          orgID={asset?.orgID}
                          perform={ASSET_MODEL_READ}
                          resource={ASSET_MODEL}
                          yes={() => (
                            <Link
                              to={{
                                pathname: `/asset-model/${templateID}`,
                                search: `?tab=editRule&RuleId=${Id}`,
                              }}
                              replace={true}
                            >
                              {Name}
                            </Link>
                          )}
                        />
                      </td>
                    </tr>
                  );
                }) ?? null}
              </tbody>
            </table>
          </div>
          <div>
            <div className="bg-background-gray p-2 pl-md-3 d-flex align-items-center">
              <button
                className="btn btn-link p-0 mr-3 text-dark"
                onClick={() =>
                  dispatchSubsTypesDisplayState({ type: 'maintenance' })
                }
                style={{ lineHeight: 1 }}
              >
                <FontAwesomeIcon
                  icon={
                    subsTypeDisplayState.showMaintenanceSubs
                      ? faChevronUp
                      : faChevronDown
                  }
                />
              </button>
              Maintenance
              <span className="ml-3">{count}</span>
            </div>
            {selectedUser?._id && (emailIsDisabled || smsIsDisabled) ? (
              <div className="text-warning three-quarter-text ml-md-3">{`User has a preference set not to receive ${
                emailIsDisabled && smsIsDisabled
                  ? 'email or SMS notifications'
                  : emailIsDisabled
                    ? 'email notifications'
                    : smsIsDisabled
                      ? 'SMS notifications or has not added a phone number'
                      : ''
              }.`}</div>
            ) : null}
            <table
              className={
                subsTypeDisplayState.showMaintenanceSubs
                  ? 'table table-borderless'
                  : 'd-none'
              }
            >
              <thead>
                <tr>
                  {showEmailSubs ? (
                    <th scope="col" className="col-notification text-center">
                      <button
                        className="btn btn-sm btn-outline-icon-link-gray bg-white text-muted rounded-lg"
                        disabled={emailIsDisabled || subscriptionPending}
                        onClick={() =>
                          handleBatchClick('email', 'maintenance-gateway')
                        }
                      >
                        Email
                      </button>
                    </th>
                  ) : null}
                  {showSmsSubs ? (
                    <th scope="col" className="col-notification text-center">
                      <button
                        className="btn btn-sm btn-outline-icon-link-gray bg-white text-muted rounded-lg"
                        disabled={smsIsDisabled || subscriptionPending}
                        onClick={() =>
                          handleBatchClick('sms', 'maintenance-gateway')
                        }
                      >
                        SMS
                      </button>
                    </th>
                  ) : null}
                  <th scope="col" className="col-asset-name"></th>
                </tr>
              </thead>
              <tbody>
                {instances?.map(
                  ({
                    _id: instance_id,
                    GtemplateReminder: { _id: reminder_id, name } = {},
                  }) => {
                    const hasRuleEmailSub = () => {
                      const { _id, Destination } = selectedUser || {};
                      if (!_id && !Destination) return false;

                      const subs = subscriptions.get(_id || Destination) || [];
                      return subs.some((s) =>
                        s.includes(`${reminder_id}:email`)
                      );
                    };
                    const hasRuleSmsSub = () => {
                      const { _id, Destination } = selectedUser || {};
                      if (!_id && !Destination) return false;

                      const subs = subscriptions.get(_id || Destination) || [];
                      return subs.some((s) => s.includes(`${reminder_id}:sms`));
                    };
                    return (
                      <tr key={instance_id}>
                        {showEmailSubs ? (
                          <td className="text-center">
                            <button
                              className={`btn btn-link p-0 ${
                                hasRuleEmailSub()
                                  ? 'text-brand-primary-alert'
                                  : 'text-background-gray'
                              }`}
                              onClick={() =>
                                handleSubscriptionClick(`${reminder_id}:email`)
                              }
                              disabled={subscriptionPending || emailIsDisabled}
                            >
                              <FontAwesomeIcon
                                icon={hasRuleEmailSub() ? faBell : faBellSlash}
                              />
                            </button>
                          </td>
                        ) : null}
                        {showSmsSubs ? (
                          <td
                            className="text-center"
                            data-tip={
                              smsIsDisabled
                                ? 'This user has a preference set to not receive SMS notifications'
                                : ''
                            }
                          >
                            <button
                              className={`btn btn-link p-0 ${
                                hasRuleSmsSub()
                                  ? 'text-brand-primary-alert'
                                  : 'text-background-gray'
                              }`}
                              onClick={() =>
                                handleSubscriptionClick(`${reminder_id}:sms`)
                              }
                              disabled={subscriptionPending || smsIsDisabled}
                            >
                              <FontAwesomeIcon
                                icon={hasRuleSmsSub() ? faBell : faBellSlash}
                              />
                            </button>
                          </td>
                        ) : null}
                        <td>
                          <Can
                            no={() => (
                              <button className="btn btn-link p-0" disabled>
                                {name}
                              </button>
                            )}
                            orgID={asset?.orgID}
                            perform={ASSET_MODEL_READ}
                            resource={ASSET_MODEL}
                            yes={() => (
                              <Link
                                to={{
                                  pathname: `/asset-model/${templateID}`,
                                  search: `?${queryString.stringify({
                                    reminder_id,
                                    tab: EDIT_REMINDER,
                                  })}`,
                                }}
                                replace={true}
                              >
                                {name}
                              </Link>
                            )}
                          />
                        </td>
                      </tr>
                    );
                  }
                ) ?? null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showSubscribeNonPlatformUserModal ? (
        <NonPlatformUserSubscriptionModal
          allowEmail
          asset={asset}
          handleNonPlatformSubAdded={handleNonPlatformSubAdded}
          isOpen={showSubscribeNonPlatformUserModal}
          toggleModal={toggleNonPlatformSubscriptionModal}
        />
      ) : null}
      <style jsx>{`
        .table {
          table-layout: ${width < 768 ? 'auto' : 'fixed'};
        }
        .col-notification {
          width: 10%;
        }
        .col-asset-name {
          width: 80%;
        }
        @media (min-width: 768px) {
          .inner-subscriptions-wrapper {
            height: 100%;
            overflow: hidden;
            display: grid;
            grid-template-columns: 1fr 3fr;
            grid-template-rows: auto;
            grid-template-areas: 'users rule-dm1-maintenance';
          }
          .users-container {
            min-height: 100%;
            overflow: auto;
            border-right: 1px solid var(--icon-link-gray);
            grid-area: users;
          }

          .rule-dm1-maintenance-wrapper {
            grid-area: rule-dm1-maintenance;
            min-height: 100%;
            overflow: auto;
          }
        }
      `}</style>
    </div>
  );
}

AssetSubscriptionsDisplay.propTypes = {
  asset: PropTypes.object,
  currentOrg: PropTypes.shape({
    Users: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        settings: PropTypes.shape({
          notifications: PropTypes.shape({
            sms: PropTypes.bool,
            email: PropTypes.bool,
          }),
        }),
      })
    ),
  }),
  currentUser: PropTypes.shape({
    _id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    settings: PropTypes.object,
  }),
  currentUserRef: PropTypes.object,
  error: PropTypes.string,
  handleNonPlatformSubAdded: PropTypes.func,
  handleNonPlatformSubRemove: PropTypes.func,
  handleSubscriptionClick: PropTypes.func,
  handleBatchClick: PropTypes.func,
  Id: PropTypes.string,
  maintenanceState: PropTypes.shape({
    count: PropTypes.number,
    instances: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        gateway_id: PropTypes.string,
        GtemplateReminder: PropTypes.shape({
          _id: PropTypes.string,
          name: PropTypes.string,
        }),
      })
    ),
  }),
  OrgId: PropTypes.string,
  ruleState: PropTypes.shape({
    Count: PropTypes.number,
    Rules: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        nickname: PropTypes.string,
      })
    ),
  }),
  selectedUser: PropTypes.shape({
    _id: PropTypes.string,
    Destination: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    settings: PropTypes.shape({
      notifications: PropTypes.shape({
        sms: PropTypes.bool,
        email: PropTypes.bool,
      }),
    }),
  }),
  setSelectedUser: PropTypes.func,
  showSubscribeNonPlatformUserModal: PropTypes.bool,
  subscriptions: PropTypes.object,
  subscriptionPending: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  template: PropTypes.shape({
    _id: PropTypes.string,
  }),
  templateID: PropTypes.string,
  toggleNonPlatformSubscriptionModal: PropTypes.func,
};

AssetSubscriptionsDisplay.defaultProps = {
  selectedUser: {},
  ruleState: { Count: 0, Rules: [] },
  subscriptions: new Map(),
};

function displayToggleReducer(state, action) {
  switch (action.type) {
    case 'dm1':
      return { ...state, showDmSubs: !state.showDmSubs };
    case 'maintenance':
      return { ...state, showMaintenanceSubs: !state.showMaintenanceSubs };
    case 'rule':
      return { ...state, showRuleSubs: !state.showRuleSubs };
    default:
      return state;
  }
}
